import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  TrendingUp,
  Users,
  DollarSign,
  BarChart3,
  Shield,
  Zap,
  Target,
  Package,
  Store,
  Globe,
  Truck,
  ShoppingBag,
  ArrowRight,
  CheckCircle2,
  Phone,
  Mail,
  Menu,
  X,
  ChevronRight,
  MapPin,
  BadgeCheck,
} from "lucide-react";

// Logo URLs
const ECOMMERCE_BROKERS_LOGO = "https://customer-assets-gfyr7b9c.emergentagent.net/job_flip-calc-1/artifacts/gxj7bze1_Ecomm%20brokers%20image.png";
const BONZA_LOGO = "https://customer-assets.emergentagent.com/job_flip-calc-1/artifacts/s4klt4h6_Colour%20Landscape%20Logo.png";

// Recently Sold Data by State (from verified sales records)
// Format: { category, price (sale price in AUD), revenue (annual revenue in AUD) }
const RECENTLY_SOLD_DATA = {
  NSW: [
    { category: "Ecommerce Health", price: 880000, revenue: 880000 },
    { category: "Ecommerce Health", price: 680000, revenue: 680000 },
    { category: "Ecommerce Health", price: 550000, revenue: 550000 },
    { category: "Ecommerce Retail", price: 405317, revenue: 405317 },
    { category: "Ecommerce Retail", price: 350000, revenue: 350000 },
    { category: "Ecommerce Retail", price: 273526, revenue: 273526 },
    { category: "Ecommerce Retail", price: 226741, revenue: 226741 },
    { category: "Ecommerce Retail", price: 218434, revenue: 218434 },
    { category: "Ecommerce Retail", price: 200000, revenue: 200000 },
    { category: "Ecommerce Retail", price: 197500, revenue: 197500 },
    { category: "Ecommerce Homewares", price: 177500, revenue: 177500 },
    { category: "Ecommerce Retail", price: 174100, revenue: 174100 },
    { category: "Ecommerce Retail", price: 150000, revenue: 150000 },
    { category: "Ecommerce Retail", price: 150000, revenue: 150000 },
    { category: "Ecommerce Retail", price: 150000, revenue: 150000 },
    { category: "Ecommerce Retail", price: 149000, revenue: 149000 },
    { category: "Ecommerce Retail", price: 135000, revenue: 135000 },
    { category: "Ecommerce Retail", price: 125000, revenue: 125000 },
    { category: "Ecommerce Babywear", price: 105000, revenue: 105000 },
    { category: "Ecommerce Retail", price: 105364, revenue: 105364 },
  ],
  QLD: [
    { category: "Ecommerce Clothing", price: 880000, revenue: 880000 },
    { category: "Ecommerce Clothing", price: 680000, revenue: 680000 },
    { category: "Ecommerce Skincare", price: 550000, revenue: 550000 },
    { category: "Ecommerce Retail", price: 405317, revenue: 405317 },
    { category: "Ecommerce Retail", price: 350000, revenue: 350000 },
    { category: "Ecommerce Retail", price: 273526, revenue: 273526 },
    { category: "Ecommerce Retail", price: 226741, revenue: 226741 },
    { category: "Ecommerce Retail", price: 218434, revenue: 218434 },
    { category: "Ecommerce Retail", price: 200000, revenue: 200000 },
    { category: "Ecommerce Retail", price: 197500, revenue: 197500 },
    { category: "Ecommerce Wholefood", price: 174100, revenue: 174100 },
    { category: "Ecommerce Skincare", price: 150000, revenue: 150000 },
    { category: "Ecommerce Swimwear", price: 150000, revenue: 150000 },
    { category: "Ecommerce Babywear", price: 149000, revenue: 149000 },
    { category: "Ecommerce Babywear", price: 135000, revenue: 135000 },
    { category: "Ecommerce Babywear", price: 125000, revenue: 125000 },
    { category: "Ecommerce Jewellery", price: 105364, revenue: 105364 },
    { category: "Ecommerce IT", price: 100417, revenue: 100417 },
    { category: "Ecommerce Babywear", price: 88000, revenue: 88000 },
    { category: "Ecommerce Candy", price: 83000, revenue: 83000 },
  ],
  VIC: [
    { category: "Ecommerce Skincare", price: 550000, revenue: 550000 },
    { category: "Ecommerce Weddings", price: 174100, revenue: 174100 },
    { category: "Ecommerce Knitting", price: 177500, revenue: 177500 },
    { category: "Ecommerce Homewares", price: 150000, revenue: 150000 },
    { category: "Ecommerce Educational", price: 150000, revenue: 150000 },
    { category: "Ecommerce Food", price: 150000, revenue: 150000 },
    { category: "Ecommerce Skincare", price: 125000, revenue: 125000 },
    { category: "Ecommerce Eco Products", price: 105000, revenue: 105000 },
    { category: "Ecommerce Homewares", price: 99000, revenue: 99000 },
    { category: "Ecommerce Health", price: 70000, revenue: 70000 },
    { category: "Ecommerce Leisure", price: 54000, revenue: 54000 },
    { category: "Ecommerce Tea", price: 46500, revenue: 46500 },
    { category: "Ecommerce Branding", price: 38457, revenue: 38457 },
    { category: "Ecommerce Homewares", price: 38000, revenue: 38000 },
    { category: "Ecommerce Alcohol", price: 34123, revenue: 34123 },
    { category: "Ecommerce Jewellery", price: 30000, revenue: 30000 },
    { category: "Ecommerce Accessories", price: 29900, revenue: 29900 },
    { category: "Ecommerce Food", price: 25000, revenue: 25000 },
    { category: "Ecommerce Wholefood", price: 22500, revenue: 22500 },
    { category: "Ecommerce Babywear", price: 15000, revenue: 15000 },
    { category: "Ecommerce Food", price: 13500, revenue: 13500 },
  ],
  WA: [
    { category: "Ecommerce Clothing", price: 105364, revenue: 105364 },
    { category: "Ecommerce Skincare", price: 100417, revenue: 100417 },
    { category: "Ecommerce Health", price: 55000, revenue: 55000 },
    { category: "Ecommerce Shoes", price: 50000, revenue: 50000 },
    { category: "Ecommerce Clothing", price: 50000, revenue: 50000 },
    { category: "Ecommerce Dancewear", price: 30000, revenue: 30000 },
    { category: "Ecommerce Babywear", price: 25000, revenue: 25000 },
  ],
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [selectedState, setSelectedState] = useState("NSW");

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleStartValuation = (buttonLocation) => {
    if (window.trackValuationStarted) {
      window.trackValuationStarted(buttonLocation);
    }
    navigate("/valuation");
  };

  const handlePhoneClick = () => {
    if (window.trackPhoneClick) {
      window.trackPhoneClick("1300 266 922");
    }
  };

  const handleEmailClick = () => {
    if (window.trackEmailClick) {
      window.trackEmailClick("sales@bonzabfs.com.au");
    }
  };

  const scrollToSection = (sectionId) => {
    setMobileMenuOpen(false);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const valuationDrivers = [
    { icon: DollarSign, title: "Sustainable Profit", description: "Consistent earnings that buyers can rely on" },
    { icon: TrendingUp, title: "Revenue Growth", description: "Year-over-year growth trajectory" },
    { icon: Users, title: "Repeat Customers", description: "Strong customer retention rates" },
    { icon: Target, title: "Customer Acquisition", description: "Efficient marketing spend and CAC" },
    { icon: BarChart3, title: "Gross Margin", description: "Healthy profit margins on products" },
    { icon: Globe, title: "Traffic Quality", description: "Diversified, sustainable traffic sources" },
    { icon: Shield, title: "Brand Strength", description: "Recognizable brand with loyal following" },
    { icon: Package, title: "Product Diversification", description: "Not dependent on single product lines" },
    { icon: Truck, title: "Supplier Stability", description: "Reliable supply chain relationships" },
    { icon: Zap, title: "Owner Independence", description: "Business can operate without owner" },
    { icon: Store, title: "Systems & Automation", description: "Efficient processes and workflows" },
    { icon: CheckCircle2, title: "Transferability", description: "Easy handover to new owner" },
  ];

  const businessTypes = [
    "Shopify Stores",
    "Amazon Businesses",
    "eBay Stores",
    "Direct-to-Consumer Brands",
    "Subscription Ecommerce",
    "Dropshipping Businesses",
    "Wholesale & Ecommerce",
    "Online Retail Brands",
    "Marketplace Businesses",
    "Multi-Channel Ecommerce",
  ];

  const faqs = [
    {
      question: "Is the valuation free?",
      answer: "Yes. The initial online valuation guide is free and without obligation."
    },
    {
      question: "Is the valuation confidential?",
      answer: "Yes. Information submitted through the valuation guide is treated as confidential and used to provide the valuation result and relevant follow-up."
    },
    {
      question: "How are ecommerce businesses valued?",
      answer: "Ecommerce businesses are commonly valued using sustainable earnings, owner earnings or EBITDA, together with factors such as growth, margins, customer quality, traffic sources, brand strength and owner dependence."
    },
    {
      question: "Is the online valuation a formal appraisal?",
      answer: "No. The result is an indicative valuation guide based on the information provided. A broker may need additional information before recommending an asking price."
    },
    {
      question: "Can you help sell my ecommerce business?",
      answer: "Yes. Ecommerce Brokers can connect the owner with an experienced broker who can assist with preparing, marketing and negotiating the sale."
    },
    {
      question: "Do you work across Australia?",
      answer: "Yes. The service is available to ecommerce business owners throughout Australia."
    },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-white shadow-md" : "bg-white/95"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <img
                src={ECOMMERCE_BROKERS_LOGO}
                alt="Ecommerce Brokers"
                className="h-10 md:h-12 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => scrollToSection("how-it-works")}
                className="text-slate-700 hover:text-blue-600 font-medium transition-colors"
              >
                How It Works
              </button>
              <button
                onClick={() => scrollToSection("what-drives-value")}
                className="text-slate-700 hover:text-blue-600 font-medium transition-colors"
              >
                What Drives Value
              </button>
              <button
                onClick={() => scrollToSection("recently-sold")}
                className="text-slate-700 hover:text-blue-600 font-medium transition-colors"
                data-testid="nav-recently-sold"
              >
                Recently Sold
              </button>
              <button
                onClick={() => scrollToSection("selling-your-business")}
                className="text-slate-700 hover:text-blue-600 font-medium transition-colors"
              >
                Selling Your Business
              </button>
              <button
                onClick={() => scrollToSection("faqs")}
                className="text-slate-700 hover:text-blue-600 font-medium transition-colors"
              >
                FAQs
              </button>
              <Button
                onClick={() => handleStartValuation("header")}
                className="bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600 text-white px-6 py-2 rounded-full font-semibold"
                data-testid="header-cta"
              >
                Start Your Valuation Guide
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t shadow-lg">
            <div className="px-4 py-4 space-y-3">
              <button
                onClick={() => scrollToSection("how-it-works")}
                className="block w-full text-left py-2 text-slate-700 hover:text-blue-600 font-medium"
              >
                How It Works
              </button>
              <button
                onClick={() => scrollToSection("what-drives-value")}
                className="block w-full text-left py-2 text-slate-700 hover:text-blue-600 font-medium"
              >
                What Drives Value
              </button>
              <button
                onClick={() => scrollToSection("recently-sold")}
                className="block w-full text-left py-2 text-slate-700 hover:text-blue-600 font-medium"
                data-testid="mobile-nav-recently-sold"
              >
                Recently Sold
              </button>
              <button
                onClick={() => scrollToSection("selling-your-business")}
                className="block w-full text-left py-2 text-slate-700 hover:text-blue-600 font-medium"
              >
                Selling Your Business
              </button>
              <button
                onClick={() => scrollToSection("faqs")}
                className="block w-full text-left py-2 text-slate-700 hover:text-blue-600 font-medium"
              >
                FAQs
              </button>
              <Button
                onClick={() => handleStartValuation("mobile-menu")}
                className="w-full bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600 text-white rounded-full font-semibold"
              >
                Start Your Valuation Guide
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-24 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div className="pt-4">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                Find Out What Your Ecommerce Business Could Be Worth
              </h1>
              <p className="text-lg md:text-xl text-slate-600 mb-8">
                Get a practical valuation guide based on your financial performance, business model, growth profile and the commercial factors that matter to ecommerce buyers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pb-4">
                <Button
                  onClick={() => handleStartValuation("hero")}
                  size="lg"
                  className="bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600 text-white px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all"
                  data-testid="hero-cta-primary"
                >
                  Start Your Valuation Guide Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => navigate("/contact")}
                  className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-4 rounded-full font-semibold text-lg"
                  data-testid="hero-cta-secondary"
                >
                  Speak With an Ecommerce Broker
                </Button>
              </div>
            </div>
            <div className="relative flex justify-center md:justify-end">
              <div className="bg-gradient-to-br from-blue-600 to-green-500 rounded-3xl p-1 max-w-xs">
                <div className="bg-white rounded-3xl p-5">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center">
                        <TrendingUp className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Revenue Growth</p>
                        <p className="text-lg font-bold text-slate-900">+32%</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-green-100 rounded-xl flex items-center justify-center">
                        <DollarSign className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Annual Profit</p>
                        <p className="text-lg font-bold text-slate-900">$185,000</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-purple-100 rounded-xl flex items-center justify-center">
                        <Users className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Repeat Customers</p>
                        <p className="text-lg font-bold text-slate-900">45%</p>
                      </div>
                    </div>
                    <div className="pt-2 border-t">
                      <p className="text-xs text-slate-500 mb-1">Indicative Valuation</p>
                      <p className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-500">
                        $550,000 - $720,000
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust and Positioning Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Specialist Valuation and Sale Support for Ecommerce Businesses
            </h2>
            <p className="text-lg text-slate-600">
              Ecommerce businesses are valued differently from traditional businesses. Understanding the key factors that drive value helps you maximise your sale price.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <BarChart3 className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Financial Performance</h3>
                <p className="text-slate-600">Revenue, profit, owner earnings, margins, and growth trajectory all impact your valuation.</p>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Customer Quality</h3>
                <p className="text-slate-600">Customer concentration, repeat-purchase revenue, and acquisition costs affect business value.</p>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Operational Strength</h3>
                <p className="text-slate-600">Traffic diversity, supplier stability, owner involvement, and transferability to a buyer.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              How It Works
            </h2>
            <p className="text-lg text-slate-600">
              Get your indicative valuation in three simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="relative">
              <div className="bg-white rounded-2xl p-8 shadow-lg h-full">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-green-500 rounded-2xl flex items-center justify-center text-white text-2xl font-bold mb-6">
                  1
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-4">
                  Complete the Valuation Guide
                </h3>
                <p className="text-slate-600">
                  Answer a series of questions about your ecommerce business, including its financial performance, revenue model, growth and operations.
                </p>
              </div>
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                <ChevronRight className="w-8 h-8 text-slate-300" />
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="bg-white rounded-2xl p-8 shadow-lg h-full">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-green-500 rounded-2xl flex items-center justify-center text-white text-2xl font-bold mb-6">
                  2
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-4">
                  Receive a Valuation Range
                </h3>
                <p className="text-slate-600">
                  Get an indicative valuation range and understand the major factors increasing or reducing your business value.
                </p>
              </div>
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                <ChevronRight className="w-8 h-8 text-slate-300" />
              </div>
            </div>

            {/* Step 3 */}
            <div>
              <div className="bg-white rounded-2xl p-8 shadow-lg h-full">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-green-500 rounded-2xl flex items-center justify-center text-white text-2xl font-bold mb-6">
                  3
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-4">
                  Speak With the Right Broker
                </h3>
                <p className="text-slate-600">
                  Request a confidential discussion with an experienced ecommerce business broker to explore your sale options.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={() => handleStartValuation("how-it-works")}
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600 text-white px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all"
              data-testid="how-it-works-cta"
            >
              Start Your Valuation Guide Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* What Drives Value */}
      <section id="what-drives-value" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              What Buyers Look for in an Ecommerce Business
            </h2>
            <p className="text-lg text-slate-600">
              Buyers generally pay more for businesses with reliable earnings, diversified revenue, repeat customers, strong margins and systems that allow the business to operate without excessive dependence on the owner.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {valuationDrivers.map((driver, index) => (
              <Card key={index} className="border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <driver.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{driver.title}</h3>
                  <p className="text-sm text-slate-500">{driver.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Business Types */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-blue-600 to-green-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Built for Ecommerce Business Owners
            </h2>
            <p className="text-lg text-blue-100">
              Our platform supports a broad range of online businesses
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {businessTypes.map((type, index) => (
              <div
                key={index}
                className="bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-full font-medium hover:bg-white/30 transition-colors"
              >
                {type}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recently Sold */}
      <section id="recently-sold" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <BadgeCheck className="w-4 h-4" />
              Proven Track Record
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Recently Sold Ecommerce Businesses
            </h2>
            <p className="text-lg text-slate-600">
              Real sales completed by our broker network across Australia
            </p>
          </div>

          {/* State Selector */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-3 bg-slate-100 p-2 rounded-full">
              <MapPin className="w-5 h-5 text-slate-500 ml-2" />
              <Select value={selectedState} onValueChange={setSelectedState}>
                <SelectTrigger className="w-[200px] border-0 bg-white rounded-full shadow-sm" data-testid="state-selector">
                  <SelectValue placeholder="Select State" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NSW">New South Wales</SelectItem>
                  <SelectItem value="QLD">Queensland</SelectItem>
                  <SelectItem value="VIC">Victoria</SelectItem>
                  <SelectItem value="WA">Western Australia</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Sales Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {RECENTLY_SOLD_DATA[selectedState].slice(0, 20).map((sale, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-green-500 rounded-lg flex items-center justify-center">
                    <BadgeCheck className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 text-sm">{sale.category}</p>
                    <p className="text-xs text-slate-500">{selectedState}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-500">
                    {formatCurrency(sale.price)}
                  </p>
                  <p className="text-xs text-slate-400">Sale Price</p>
                </div>
              </div>
            ))}
          </div>

          {/* Show More / Stats */}
          {RECENTLY_SOLD_DATA[selectedState].length > 20 && (
            <div className="text-center mt-8">
              <p className="text-slate-500 mb-4">
                Showing 20 of {RECENTLY_SOLD_DATA[selectedState].length} sales in {selectedState === "NSW" ? "New South Wales" : selectedState === "QLD" ? "Queensland" : selectedState === "VIC" ? "Victoria" : "Western Australia"}
              </p>
            </div>
          )}

          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
            <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-xl p-6 text-center">
              <p className="text-3xl font-bold text-slate-900">
                {Math.min(RECENTLY_SOLD_DATA.NSW.length, 20) + Math.min(RECENTLY_SOLD_DATA.QLD.length, 20) + Math.min(RECENTLY_SOLD_DATA.VIC.length, 20) + Math.min(RECENTLY_SOLD_DATA.WA.length, 20)}+
              </p>
              <p className="text-sm text-slate-600">Businesses Sold</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-xl p-6 text-center">
              <p className="text-3xl font-bold text-slate-900">4</p>
              <p className="text-sm text-slate-600">States Covered</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-xl p-6 text-center">
              <p className="text-3xl font-bold text-slate-900">$15K</p>
              <p className="text-sm text-slate-600">Min Sale Price</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-xl p-6 text-center">
              <p className="text-3xl font-bold text-slate-900">$880K</p>
              <p className="text-sm text-slate-600">Max Sale Price</p>
            </div>
          </div>

          <div className="text-center mt-10">
            <Button
              onClick={() => handleStartValuation("recently-sold")}
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600 text-white px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all"
              data-testid="recently-sold-cta"
            >
              Find Out What Your Business Could Be Worth
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Selling Your Business */}
      <section id="selling-your-business" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Ready to Explore a Sale?
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Ecommerce Brokers can connect you with a broker who understands ecommerce businesses and can assist with the entire sale process.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  "Preparing your business for sale",
                  "Determining an appropriate asking price",
                  "Preparing a confidential business profile",
                  "Marketing the opportunity",
                  "Qualifying buyers",
                  "Managing confidentiality",
                  "Negotiating offers",
                  "Supporting the transaction through to completion",
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>

              <p className="text-slate-500 text-sm mb-6 italic">
                Initial discussions are confidential and without obligation.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => handleStartValuation("selling-section")}
                  size="lg"
                  className="bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600 text-white px-6 py-3 rounded-full font-semibold"
                  data-testid="selling-cta-primary"
                >
                  Start Your Valuation Guide
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => navigate("/book")}
                  className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-6 py-3 rounded-full font-semibold"
                  data-testid="selling-cta-secondary"
                >
                  Book a Confidential Broker Call
                </Button>
              </div>
            </div>
            
            <div className="bg-slate-50 rounded-3xl p-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-6">Why Use Ecommerce Brokers?</h3>
              <div className="space-y-4">
                {[
                  "Ecommerce-focused valuation process",
                  "Practical valuation guidance",
                  "Experienced Australian business brokers",
                  "Confidential and obligation-free",
                  "National buyer reach",
                  "Support from valuation through to sale",
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 pt-6 border-t border-slate-200">
                <p className="text-sm text-slate-500 mb-3">Powered by</p>
                <img src={BONZA_LOGO} alt="Bonza Business & Franchise Sales" className="h-10" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section id="faqs" className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Frequently Asked Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-white rounded-xl border-0 shadow-sm">
                <AccordionTrigger className="px-6 py-4 text-left font-semibold text-slate-900 hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-slate-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="text-center mt-8 p-6 bg-white rounded-2xl shadow-sm">
            <p className="text-slate-600 mb-4">
              <strong>Important:</strong> The online valuation provides an indicative guide based on the information provided. A more detailed broker appraisal may be required before setting an asking price.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Find Out What Your Business Could Be Worth?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Get your free, confidential valuation guide in minutes
          </p>
          <Button
            onClick={() => handleStartValuation("footer-cta")}
            size="lg"
            className="bg-gradient-to-r from-blue-500 to-green-400 hover:from-blue-600 hover:to-green-500 text-white px-10 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all"
            data-testid="footer-cta"
          >
            Start Your Valuation Guide Now
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <img src={ECOMMERCE_BROKERS_LOGO} alt="Ecommerce Brokers" className="h-10 mb-4 brightness-0 invert" />
              <p className="mb-4">
                Ecommerce Brokers — Powered by Bonza Business & Franchise Sales
              </p>
              <img src={BONZA_LOGO} alt="Bonza Business & Franchise Sales" className="h-8" />
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection("how-it-works")} className="hover:text-white transition-colors">How It Works</button></li>
                <li><button onClick={() => scrollToSection("what-drives-value")} className="hover:text-white transition-colors">What Drives Value</button></li>
                <li><button onClick={() => scrollToSection("recently-sold")} className="hover:text-white transition-colors">Recently Sold</button></li>
                <li><button onClick={() => scrollToSection("selling-your-business")} className="hover:text-white transition-colors">Selling Your Business</button></li>
                <li><button onClick={() => scrollToSection("faqs")} className="hover:text-white transition-colors">FAQs</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <ul className="space-y-2">
                <li>
                  <a
                    href="tel:1300266922"
                    onClick={handlePhoneClick}
                    className="flex items-center gap-2 hover:text-white transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    1300 266 922
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:sales@bonzabfs.com.au"
                    onClick={handleEmailClick}
                    className="flex items-center gap-2 hover:text-white transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    sales@bonzabfs.com.au
                  </a>
                </li>
              </ul>
              <p className="mt-4 text-sm">
                Australia-wide service
              </p>
            </div>
          </div>
          
          <div className="border-t border-slate-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm">
                © {new Date().getFullYear()} Ecommerce Brokers. All rights reserved.
              </p>
              <div className="flex gap-6 text-sm">
                <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
                <Link to="/terms" className="hover:text-white transition-colors">Terms and Conditions</Link>
                <Link to="/disclaimer" className="hover:text-white transition-colors">Valuation Disclaimer</Link>
                <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Mobile Sticky CTA */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-40">
        <Button
          onClick={() => handleStartValuation("mobile-sticky")}
          className="w-full bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600 text-white py-3 rounded-full font-semibold"
          data-testid="mobile-sticky-cta"
        >
          Start Your Valuation Guide
        </Button>
      </div>
    </div>
  );
};

export default LandingPage;
